<template>
  <div ref="wrapper">
    <div v-if="field.label && !isModal" class="row align-items-center">
      <div class="col">
        <label class="mb-4">
          <b>{{ label }}</b>
          <span v-if="field.required" class="red--text">*</span>
          <i
            v-if="field.helpSnippet"
            v-b-popover.hover.top="$t(field.helpSnippet)"
            class="fal fa-question-circle ml-1"
          />
        </label>
      </div>
    </div>
    <div class="d-flex flex-nowrap">
      <div style="flex-grow: 1; max-width: 100%">
        <div>
          <h4 v-if="elementSelectorActive">
            {{ $t("workflowElements.clickOnTheElement") }}
          </h4>
          <div v-if="selectedNodes.length">
            <div
              v-for="node in selectedNodes"
              :key="node.attrs.id"
              class="d-flex align-items-center flex-wrap mb-8"
            >
              <div
                class="cursor-pointer symbol symbol-50 symbol-light mr-5"
                @click="blinkElementSelectorNode(node)"
              >
                <span class="symbol-label">
                  <img
                    :src="node.attrs.fillPatternImage.src"
                    class="h-50 align-self-center"
                    alt=""
                  />
                </span>
              </div>
              <div class="d-flex flex-column flex-grow-1 mr-2">
                <a
                  class="font-weight-bold text-dark-75 text-hover-primary font-size-lg mb-1"
                  @click="blinkElementSelectorNode(node)"
                >
                  {{ node.attrs.data.planning.label }}
                </a>
                <span
                  class="cursor-pointer text-muted font-weight-bold"
                  @click="blinkElementSelectorNode(node)"
                >
                  {{ node.attrs.label }}
                </span>
              </div>
              <button
                class="btn btn-primary mt-1 my-lg-0 my-2"
                @click="initiateReplaceNode(node)"
              >
                <i class="fal fa-pencil" />
              </button>
              <button
                class="btn btn-danger mt-1 my-lg-0 my-2 ml-1"
                @click="removeNode(node)"
              >
                <i class="fal fa-trash-can icon-lg pr-0" />
              </button>
            </div>
          </div>
          <div
            v-if="selectMultiple ? selectMultiple : selectedNodes.length <= 0"
          >
            <b-button
              v-if="!elementSelectorActive"
              variant="primary"
              @click="selectNodeElement"
            >
              {{ $t("workflowElements.selectElement") }}
            </b-button>
            <b-button
              v-if="elementSelectorActive"
              variant="primary"
              @click="selectNodeElementAbort"
            >
              {{ $t("general.cancel") }}
            </b-button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { bus } from "@/main";
import store from "@/core/services/store";
import { Toast } from "@/core/plugins/swal";
import i18n from "@/core/plugins/vue-i18n";

export default {
  props: {
    field: {
      type: Object
    },
    configValues: {
      type: Array
    },
    outputValues: {
      type: Array,
      default: () => []
    },
    errorValues: {
      type: Array,
      default: () => []
    },
    debugValues: {
      type: Array,
      default: () => []
    },
    areaInvalid: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    node: {
      type: Object,
      default: () => {}
    },
    conditionValue: {
      type: Boolean,
      default: false
    },
    isModal: {
      type: Boolean,
      default: false
    },
    allowOnly: {
      type: Array,
      default: () => []
    },
    selectMultiple: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      edited: false,
      value: [],
      selectedNodes: [],
      elementSelectorStrokeOriginalColor: "#9babbd",
      elementSelectorStrokeColorInterval: null,
      elementSelectorActive: false,
      nodeToReplace: null
    };
  },
  computed: {
    label: function () {
      const locale = store.getters.language;

      return this.field.label[locale ?? "de-de"];
    }
  },
  watch: {
    value: function () {
      this.edited = true;
      this.$set(this.field, "value", this.value);
      this.$emit("change", this.value);
    }
  },
  mounted() {
    this.value = this.field.value ?? [];
    this.selectedNodes = this.$root.$refs.WFDEditor.nodes.filter(x =>
      this.value.includes(x.attrs.id)
    );
    bus.$on("nodeSelected", this.nodeSelected);
  },
  beforeDestroy() {
    this.blinkElementSelectorNodeStop();
  },
  destroyed() {
    bus.$off("nodeSelected", this.nodeSelected);
  },
  methods: {
    blinkElementSelectorNode(node) {
      if (this.elementSelectorStrokeColorInterval) {
        return;
      }
      this.elementSelectorStrokeColorInterval = setInterval(() => {
        let newColor =
          node.attrs.stroke === this.elementSelectorStrokeOriginalColor
            ? "#ff0000"
            : this.elementSelectorStrokeOriginalColor;
        node.stroke(newColor);
        this.$root.$refs.WFDEditor.stage.batchDraw();
      }, 150);
      setTimeout(() => {
        this.blinkElementSelectorNodeStop();
      }, 3000);
    },
    blinkElementSelectorNodeStop() {
      if (!this.selectedNodes.length) {
        return;
      }
      clearInterval(this.elementSelectorStrokeColorInterval);
      this.elementSelectorStrokeColorInterval = null;
      this.selectedNodes.forEach(node => {
        node.stroke(this.elementSelectorStrokeOriginalColor);
      });
      this.$root.$refs.WFDEditor.stage.batchDraw();
    },
    selectNodeElement() {
      this.nodeToReplace = null;
      this.elementSelectorActive = true;
      document.getElementById("konva").style.cursor = "crosshair";
      this.$root.$refs.WFDEditor.preventNodeClick = true;
    },
    selectNodeElementAbort() {
      this.nodeToReplace = null;
      this.elementSelectorActive = false;
      document.getElementById("konva").style.cursor = "pointer";
      this.$root.$refs.WFDEditor.preventNodeClick = false;
    },
    nodeSelected(node) {
      // Exit if element selector is not active
      if (!this.elementSelectorActive) return;

      // Check if node is already selected
      const nodeExists = this.selectedNodes.some(
        n => n.attrs.id === node.attrs.id
      );
      if (nodeExists) {
        // If node is already selected, deactivate selector and reset cursor
        this.resetSelector();
        return;
      }

      // Set node stroke to original color
      node.stroke(this.elementSelectorStrokeOriginalColor);

      if (this.nodeToReplace) {
        // Replace the node if nodeToReplace is defined
        const index = this.selectedNodes.findIndex(
          n => n.attrs.id === this.nodeToReplace.attrs.id
        );

        if (index !== -1 && !this.checkIfNodeCanOnlyExistOnce(node)) {
          this.selectedNodes.splice(index, 1, node);
          this.value.splice(index, 1, node.attrs.id);
        }
      } else {
        // Handle node selection based on allowed elements
        this.handleNodeSelection(node);
      }

      // Reset selector state
      this.resetSelector();
    },
    resetSelector() {
      this.elementSelectorActive = false;
      document.getElementById("konva").style.cursor = "pointer";
      this.$root.$refs.WFDEditor.preventNodeClick = false;
    },
    handleNodeSelection(node) {
      const isAllowedByProp =
        this.allowOnly.length &&
        this.allowOnly.includes(node.attrs.data.flow_element_name);

      if (this.checkIfNodeCanOnlyExistOnce(node)) return;

      if (
        (this.allowOnly.length && isAllowedByProp) ||
        !this.allowOnly.length
      ) {
        this.selectedNodes.push(node);

        // if !selectMultiple, save element hash as string instead of array
        this.selectMultiple
          ? this.value.push(node.attrs.id)
          : (this.value = node.attrs.id);
      }
    },
    checkIfNodeCanOnlyExistOnce(node) {
      if (this.selectMultiple) {
        const waitingElements = this.$root.$refs.WFDEditor.nodes.filter(
          x => x.attrs.data.flow_element_name === "waitForChildren"
        );

        let canOnlyExistsOnce = false;
        canOnlyExistsOnce = waitingElements.some(y =>
          y.attrs.data.configuration[0].value?.includes(node.attrs.id)
        );

        if (canOnlyExistsOnce) {
          Toast.fire({
            icon: "error",
            title: i18n.t("workflowElements.canOnlyBeReferencedOnce")
          });
          return true;
        }

        return false;
      }
    },
    initiateReplaceNode(node) {
      this.nodeToReplace = node;
      this.elementSelectorActive = true;
      document.getElementById("konva").style.cursor = "crosshair";
      this.$root.$refs.WFDEditor.preventNodeClick = true;
    },
    removeNode(node) {
      // Remove from selectedNodes array
      this.selectedNodes = this.selectedNodes.filter(n => n !== node);

      // Remove from value array if selectMultiple is true
      if (this.selectMultiple && typeof this.value !== "string") {
        this.value = this.value.filter(id => id !== node.attrs.id);
      } else if (this.value === node.attrs.id) {
        // Clear the value if it matches the node's id and selectMultiple is false
        this.value = this.selectMultiple ? [] : "";
      }
    }
  }
};
</script>
